import React from 'react';
import { Link } from 'react-router-dom';
import { Fish, Drumstick, Egg, Milk, Carrot, ShoppingBag, Waves } from 'lucide-react';
import { focusRing } from '../styles/ui';

// The API returns a fixed icon-hint string per admin category; mapping it to
// an actual icon is a frontend concern. Unmapped hints fall back to ShoppingBag.
// Kept as the fallback visual for any category without a matching photo below.
const ICON_BY_HINT = {
  meat: Drumstick,
  seafood: Fish,
  fish: Waves,
  eggs: Egg,
  dairy: Milk,
  groceries: ShoppingBag,
  vegetables: Carrot,
  'storefront-outline': ShoppingBag,
};

// categoryKey is `toCategoryKey(adminLabel)` from the backend (see
// freshnhomely-backend/src/utils/categorySlugs.js) — matching on it, rather
// than the shared iconHint, keys each photo to exactly one admin category.
const IMAGE_BY_CATEGORY_KEY = {
  chicken: '/chicken1.jpg',
  eggs: '/eggs.jpg',
  'mutton-goat-lamb': '/mutton.jpg',
  seafood: '/fish.jpg',
  groceries: '/grocery.jpg',
  'fresh-fruits-and-vegetables': '/fruits.jpg',
  others: '/others.jpg',
};

// Extremely subtle pastel identity per category, applied to the fallback
// icon tile (no photo) and the card body beneath the image — never behind
// the photo itself, so the product photography stays the visual focus.
const TINT_BY_CATEGORY_KEY = {
  chicken: 'from-amber-50/60 to-white',
  'mutton-goat-lamb': 'from-rose-50/50 to-white',
  seafood: 'from-sky-50/60 to-white',
  'fresh-fruits-and-vegetables': 'from-emerald-50/60 to-white',
  groceries: 'from-orange-50/40 to-white',
  eggs: 'from-amber-50/40 to-white',
  others: 'from-violet-50/40 to-white',
};

const CategoryCard = ({ categoryKey, label, count, isEmpty, iconHint }) => {
  const Icon = ICON_BY_HINT[iconHint] || ShoppingBag;
  const image = IMAGE_BY_CATEGORY_KEY[categoryKey];
  const tint = TINT_BY_CATEGORY_KEY[categoryKey] || 'from-emerald-50/40 to-white';

  const cardBody = (
    <div
      className={`bg-gradient-to-b ${tint} rounded-3xl border border-emerald-100/70 shadow-soft flex flex-col overflow-hidden transition-shadow duration-300 ${
        isEmpty ? 'opacity-60' : 'card-hover group-hover:border-emerald-200'
      }`}
    >
      <div className="relative h-[120px] sm:h-[160px] lg:h-[200px] bg-gradient-to-br from-emerald-50 to-emerald-100 overflow-hidden">
        {image ? (
          <img
            src={image}
            alt={label}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <div className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br ${tint}`}>
            <Icon className="text-emerald-400 w-10 h-10" aria-hidden="true" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-primary scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" aria-hidden="true" />
      </div>

      <div className="p-4">
        <h3 className="font-bold text-emerald-950 text-sm sm:text-base leading-tight break-words mb-1">{label}</h3>
        {isEmpty ? (
          <span className="text-xs sm:text-sm text-gray-400 leading-relaxed">Coming soon</span>
        ) : (
          <p className="text-xs sm:text-sm text-ink-secondary leading-relaxed">
            {count} product{count === 1 ? '' : 's'}
          </p>
        )}
      </div>
    </div>
  );

  if (isEmpty) return cardBody;

  return (
    <Link to={`/categories/${categoryKey}`} className={`group block ${focusRing}`}>
      {cardBody}
    </Link>
  );
};

export default CategoryCard;
